import { IconButton, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "esseal-data-table";
import OutgoingMailIcon from '@mui/icons-material/OutgoingMail';
import { CompanyResource, PermissionActionEnum, PermissionEntityEnum } from "@/graphql/generated/graphql";
import { titleizeCamelCase } from "@/utils/helperFunctions";
import EssealPhoneInput from "@/components/EssealPhoneInput";
import PermissionContext from "@/Contexts/Permissions";
import { useContext } from "react";
import RequestPermissionTooltip from "@/components/RequestPermissionTooltip";

const subContractorsColumns = (handleCreateInvitation: (id: string) => void): GridColDef<CompanyResource>[] => {
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { SubcontractorManagement } = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, SubcontractorManagement);

  return [
    {
      field: "name",
      headerName: "Name",
      width: 300,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Tooltip title={row.name}>
          <span>{row.name}</span>
        </Tooltip>
      ),
    },
    {
      field: "pointOfContact",
      headerName: "Point Of Contact",
      width: 300,
      valueGetter: (row) => row.resourceMeta?.pointOfContact || "",
      renderCell: (params) => {
        return titleizeCamelCase(params.row.resourceMeta?.pointOfContact || "");
      },
    },
    {
      field: "pointOfContactEmail",
      headerName: "Point Of Contact Email",
      width: 300,
      valueGetter: (row) => row.resourceMeta?.email || "",
      renderCell: (params) => {
        return params.row.resourceMeta?.email || "";
      },
    },
    {
      field: "pointOfContactPhone",
      headerName: "Point Of Contact Phone",
      width: 300,
      valueGetter: (row) => row.resourceMeta?.primaryPhone || "",
      renderCell: ({ row }) => (
        <EssealPhoneInput
          readOnly
          countryCode="GB"
          defaultValue={row.resourceMeta?.primaryPhone || ""}
          showCountrySelector={false}
          theme={{ colorBorder: "transparent", colorBg: "transparent" }}
        />
      ),
    },
    {
      field: "invite",
      headerName: "Invite",
      width: 150,
      renderCell: ({ row }: GridRenderCellParams<CompanyResource>) => {
        if (!row.canInviteSubContractor) return <></>;

        // Technically should have a SendEmail permission but the Registry (at the time of writing) does not have one
        // for SubcontractorManagement. Hence, falling back to Manage.
        return (
          <RequestPermissionTooltip
            hasPermission={canPerformAction(Manage, SubcontractorManagement)}
            requiredPermission={{ action: Manage, entity: SubcontractorManagement }}
            checkRequestExists={checkRequestExists}
            onRequest={requestPermission}
          >
            <IconButton onClick={() => handleCreateInvitation(row.id)} disabled={!hasPermission}>
              <OutgoingMailIcon
                fontSize="small"
                color={hasPermission ? "primary" : "disabled"}
              />
            </IconButton>
          </RequestPermissionTooltip>
        );
      },
    },
  ];
};

export default subContractorsColumns;
